<div class="cvdp-selector" [class.cvdp-selector--compact]="isCompact" #dock>

  <!-- Compact selector bar -->
  <div class="cvdp-selector__bar d-print-none">
    <div class="container">
      <div class="cvdp-selector__error" *ngIf="selectorError">
        <i class="fas fa-exclamation-triangle me-2" aria-hidden="true"></i>
        <span>Time periods and areas could not be loaded.</span>
        <button type="button" class="cvdp-selector__retry" (click)="initialiseSelector()">Try again</button>
      </div>

      <ng-container *ngIf="!selectorError && !isCompact" [ngTemplateOutlet]="selectorControls"></ng-container>

      <!-- Phones and tablets: the controls themselves live in the sheet, and this is
           the line of context that stays on screen while the page is scrolled. -->
      <button type="button" class="cvdp-selector__dock-trigger" *ngIf="!selectorError && isCompact"
        (click)="openSheet()" aria-haspopup="dialog" [attr.aria-expanded]="sheetOpen"
        [attr.aria-label]="dockAriaLabel">
        <span class="cvdp-selector__dock-text">
          <span class="cvdp-selector__dock-area">{{ displayableSelectedArea ? displayableSelectedArea.AreaName : 'Select an area' }}</span>
          <span class="cvdp-selector__dock-meta">{{ dockSummary }}</span>
        </span>
        <span class="cvdp-selector__dock-cta" aria-hidden="true">
          <i class="fas fa-sliders-h me-2"></i>Change
        </span>
      </button>
    </div>
  </div>

  <!-- Current selection -->
  <div class="cvdp-selector__banner" id="strategicView-timePeriod-area"
    *ngIf="displayableSelectedArea">
    <div class="container cvdp-selector__banner-inner">
      <div class="cvdp-selector__banner-area">
        {{ displayableSelectedArea.AreaCode }}: <strong>{{ displayableSelectedArea.AreaName }}</strong>
      </div>
      <ng-container [ngTemplateOutlet]="selectionFacts"></ng-container>
    </div>
  </div>

  <!-- Modelled data notice -->
  <div class="cvdp-selector__notice" *ngIf="dataType == 'modelled'" role="status">
    <div class="container">
      <strong>You are viewing modelled data. </strong>
      <span class="cvdp-selector__notice-detail">{{ modelledNotice }}</span>
    </div>
  </div>

  <!-- No data for this period and area combination -->
  <div class="cvdp-selector__banner cvdp-selector__banner--error" *ngIf="!isSelectedAreaInTheSelectedTimePeriod">
    <div class="container cvdp-selector__banner-inner">
      <div class="cvdp-selector__banner-message">
        <i class="fas fa-exclamation-triangle me-3" aria-hidden="true"></i>
        <div>
          <div class="fw-bold">{{ noDataBannerTitle }}</div>
          <div>{{ noDataBannerHint }}</div>
        </div>
      </div>
      <button type="button" class="btn btn-sm btn-dark d-print-none" (click)="revertSelection()">
        {{ noDataBannerActionLabel }}
      </button>
    </div>
  </div>

</div>

<!-- The controls, rendered either in the bar above or in the sheet below, never both -->
<ng-template #selectorControls>
  <div class="cvdp-selector__controls">

    <div class="cvdp-selector__field">
      <span class="cvdp-selector__label">Select a time period:</span>
      <app-period-dropdown
        [periods]="periods"
        [selectedPeriod]="selectedPeriod"
        [helperText]="periodHelperText"
        [loading]="periodsLoading"
        [busy]="selectorLoading"
        [disabled]="checkingIfAreaExists"
        [inline]="isCompact"
        (opened)="onPeriodDropdownOpened()"
        (periodSelected)="onPeriodSelected($event)">
      </app-period-dropdown>
    </div>

    <div class="cvdp-selector__field">
      <span class="cvdp-selector__label">Select a system and area:</span>
      <app-area-dropdown
        [systemLevels]="systemLevels"
        [activeLevel]="activeSystemLevel"
        [areas]="activeSystemLevel?.SystemLevelAreas"
        [areasLoading]="activeSystemLevel?.SystemLevelAreasLoading"
        [selectedArea]="displayableSelectedArea"
        [levelsLoading]="levelsLoading"
        [searchControl]="partialAreaName"
        [searchResults]="searchResults"
        [searchLoading]="areasLoading"
        [searchError]="searchError"
        [busy]="selectorLoading"
        [disabled]="checkingIfAreaExists"
        [inline]="isCompact"
        (opened)="onAreaDropdownOpened()"
        (levelActivated)="onLevelActivated($event)"
        (areaSelected)="onAreaSelected($event)">
      </app-area-dropdown>
    </div>

    <div class="cvdp-selector__field cvdp-selector__field--auto" *ngIf="showDataTypeToggle">
      <span class="cvdp-selector__label" id="cvdpDataTypeLabel">Data type:</span>
      <div class="cvdp-segmented" role="radiogroup" aria-labelledby="cvdpDataTypeLabel">
        <button #dataTypeOption type="button" role="radio" class="cvdp-segmented__option" *ngFor="let opt of dataTypeToggleOptions; trackBy: trackByOptionKey"
          [class.cvdp-segmented__option--active]="dataType == opt.key"
          [attr.aria-checked]="dataType == opt.key"
          [tabindex]="dataType == opt.key ? 0 : -1"
          (click)="setDataType(opt.key)"
          (keydown)="onDataTypeKeydown($event)">
          {{ opt.name }}
        </button>
      </div>
    </div>

    <div class="cvdp-selector__status" *ngIf="checkingIfAreaExists">
      <i class="fas fa-spinner fa-spin me-2" aria-hidden="true"></i>Checking this area...
    </div>

  </div>
</ng-template>

<!-- Coverage for the selected area: in the banner on desktop, in the sheet on mobile -->
<ng-template #selectionFacts>
  <div class="cvdp-selector__facts">
    <div *ngIf="selectedPeriod" class="cvdp-selector__fact">
      <span class="cvdp-selector__fact-label">Time period</span>
      <span class="cvdp-selector__fact-value">{{ selectedPeriod.TimePeriodName }}</span>
    </div>
    <div *ngIf="selectedArea?.ParticipationRate" class="cvdp-selector__fact">
      <span class="cvdp-selector__fact-label">Participation coverage</span>
      <span class="cvdp-selector__fact-value">{{ selectedArea.ParticipationRate }}%</span>
    </div>
    <ng-container *ngIf="selectedPeriod && showPopulationRate(selectedPeriod.TimePeriodID)">
      <div *ngIf="selectedArea?.PopulationRate" class="cvdp-selector__fact">
        <span class="cvdp-selector__fact-label">Population coverage</span>
        <span class="cvdp-selector__fact-value">{{ selectedArea.PopulationRate }}%</span>
      </div>
    </ng-container>
    <!-- Outcomes periods publish no coverage rates, so there is nothing to explain. -->
    <a href="/methodology#coverage" target="_blank" class="cvdp-selector__fact-help"
      *ngIf="selectedArea?.SystemLevelID !== 5 && (selectedArea?.ParticipationRate || selectedArea?.PopulationRate)"
      [ngbTooltip]="'Click here to learn more about coverage'">
      <i class="fal fa-question-circle" aria-hidden="true"></i>
      <span class="visually-hidden">Learn more about coverage</span>
    </a>
  </div>
</ng-template>

<!-- Bottom sheet, opened from the docked bar. Only ever used below the breakpoint. -->
<ng-template #selectorSheet>
  <div class="cvdp-sheet__header">
    <h2 class="cvdp-sheet__title" id="cvdpSheetTitle">Time period and area</h2>
    <button type="button" class="cvdp-sheet__close" (click)="closeSheet()" aria-label="Close">
      <i class="fas fa-times" aria-hidden="true"></i>
    </button>
  </div>

  <div class="cvdp-sheet__body">
    <ng-container [ngTemplateOutlet]="selectorControls"></ng-container>

    <p class="cvdp-sheet__note" *ngIf="dataType == 'modelled'">{{ modelledNotice }}</p>

    <div class="cvdp-sheet__selection" *ngIf="selectedArea && isSelectedAreaInTheSelectedTimePeriod">
      <span class="cvdp-sheet__selection-title">{{ selectedArea.AreaCode }}: {{ selectedArea.AreaName }}</span>
      <ng-container [ngTemplateOutlet]="selectionFacts"></ng-container>
    </div>
  </div>

  <div class="cvdp-sheet__footer">
    <button type="button" class="cvdp-sheet__done" (click)="closeSheet()">Done</button>
  </div>
</ng-template>
